import { CalendarEvent, NativeAppleEvent, TaskItem } from '@demind-inc/core';
import {
  useAddTodoTask,
  UseAddTodoTaskParams,
  useDeleteTask,
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
  useUpdateTaskStatus,
  useUpdateTodoTask,
  useDeleteCalendarEvent,
} from '../mutations';
import { useCalendarEventsCache, usePreferences, useTodoTasksCache } from '../queries';
import { useAuthContext, useCalendarContext, useTodoTasksContext } from '../context';
import {
  CalendarEventModifyOption,
  FirestoreTaskItem,
  TaskModifyOption,
  UseUpdateTodoTaskParams,
} from '../types';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  determineTaskEventAction,
  getDueDateYYYYMMDD,
  isAppleEvent,
  transformTaskTimeToCreateCalendarEventTime,
} from '../../helpers';
import { transformTaskLabelsToEventCategories } from '../../helpers/eventCategory.helpers';
import { getCssVariable, trackEventMixpanel } from '../../utils';
import { useRecoilState } from 'recoil';
import { eventsSnackBarAtom } from '../recoil';
import {
  deleteRecurringEventFromAppleCalendarDesktop,
  extractNativeAppleEventId,
  isAppleCalendarDesktopSupported,
  saveEventToAppleCalendarDesktop,
  transformEventForAppleCalendarDesktop,
  updateEventInAppleCalendarDesktop,
} from '../../helpers/appleCalendarDesktop.helpers';
import {
  isAppleRemindersDesktopSupported,
  createAppleReminderDesktop,
  updateAppleReminderDesktop,
  deleteAppleReminderDesktop,
  completeAppleReminderDesktop,
  mapTaskPriorityToApplePriority,
  recurrenceStringToAppleRule,
} from '../../helpers/appleRemindersDesktop.helpers';
import { translateI18n } from '../../i18n';

interface DeleteTodoTaskParams {
  taskId: string;
  boardId: string;
  originalTaskId?: string;
  mutateParam?: {
    date: string;
    isOverdue: boolean;
    parentOriginalTaskId?: string;
  };
  shouldReflectToEvent?: boolean;
  dueDateTime?: TaskItem['dueDateTime'];
}

interface UpdateTodoTaskStatusParams {
  taskId: string;
  boardId: string;
  completed: boolean;
  originalTaskId?: string;
  mutateParams?: {
    date?: string;
    parentOriginalTaskId?: string;
  };
}

type AddTodoTaskParams = Omit<UseAddTodoTaskParams, 'userId'> & { shouldReflectToEvent?: boolean };

const primaryColor = getCssVariable('--color-primary');

// Build common event data for task events
const buildTaskEventData = (
  taskInfo: Partial<FirestoreTaskItem> | TaskModifyOption,
  boardId: string,
  taskId: string,
  appFrom: string,
  options: {
    eventId?: string;
    unSyncToCalendar?: boolean;
    description:
      | 'Created from Lifestack (https://lifestack.ai)'
      | 'Updated from Lifestack (https://lifestack.ai)';
    includeEventTime?: boolean;
  }
): CalendarEventModifyOption | null => {
  const eventTime =
    options.includeEventTime && taskInfo.dueDateTime?.datetime && taskInfo.duration?.amount
      ? transformTaskTimeToCreateCalendarEventTime(taskInfo as TaskModifyOption)
      : undefined;

  // For create, eventTime is required
  if (options.includeEventTime && !eventTime) {
    return null;
  }
  const categories = transformTaskLabelsToEventCategories(taskInfo.labels);

  const eventData: CalendarEventModifyOption = {
    ...(options.eventId && { eventId: options.eventId }),
    summary: taskInfo.name ?? '',
    description: options.description,
    categories,
    taskFrom: {
      taskId,
      boardId,
      from: appFrom as any,
      autoSuggestedReason: taskInfo.dueDateTime?.autoSuggestedReason,
    },
    scheduleFrom: 'todo' as const,
    autoScheduleFrom: {
      from: 'todo',
      id: taskId,
      predicted: false,
      editedManually: true,
    },
    unSyncToCalendar: options.unSyncToCalendar === undefined ? true : !!options.unSyncToCalendar,
    ...(eventTime || {}),
  };

  return eventData;
};

const getAddedToCalendar = (
  shouldReflectToEvent: boolean,
  taskInfo?: Partial<FirestoreTaskItem> | TaskModifyOption
) => {
  return (
    !!shouldReflectToEvent && !!taskInfo?.dueDateTime?.datetime && !!taskInfo?.duration?.amount
  );
};

export const useTaskActions = () => {
  const { updateTodoTask: updateTodoTaskMutation, isUpdating: isUpdatingTask } =
    useUpdateTodoTask();
  const { addTodoTask: addTodoTaskMutation, isAdding: isAddingTask } = useAddTodoTask();
  const { deleteTask: deleteTodoTaskMutation, isDeleting: isDeletingTask } = useDeleteTask();
  const { updateTodoTaskStatus: updateTodoTaskStatusMutation, isTaskStatusUpdating } =
    useUpdateTaskStatus();
  const { findProject } = useTodoTasksContext();
  const { user } = useAuthContext();
  const { findInCache: findEventsInCache, findInCacheByDate: findEventsInCacheByDate } =
    useCalendarEventsCache();
  const { findInCacheByDate } = useTodoTasksCache();
  const { updateCalendarEvent } = useUpdateCalendarEvent();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);
  const { mainCalendar, calendarPreferences, findCalendarItem } = useCalendarContext();
  const { createCalendarEvent: createCalendarEventV2Async } = useCreateCalendarEvent();
  const { deleteCalendarEvent } = useDeleteCalendarEvent();
  const canUseAppleDesktopBridge = isAppleCalendarDesktopSupported();

  // Create calendar event for a task
  const createTaskEvent = async (
    taskInfo: Partial<FirestoreTaskItem> | TaskModifyOption,
    boardId: string,
    taskId: string,
    appFrom: string
  ) => {
    if (!getAddedToCalendar(true, taskInfo) || !mainCalendar?.calendarId || !user?.userId) {
      return;
    }

    const eventData = buildTaskEventData(taskInfo, boardId, taskId, appFrom, {
      description: 'Created from Lifestack (https://lifestack.ai)',
      includeEventTime: true,
      unSyncToCalendar: calendarPreferences?.unSyncToCalendar?.task,
    });

    if (!eventData) {
      return;
    }

    const targetCalendar = findCalendarItem(mainCalendar.calendarId);
    const shouldCreateInAppleCalendar =
      canUseAppleDesktopBridge && isAppleEvent(mainCalendar.calendarId, findCalendarItem);

    let createdAppleEvent: NativeAppleEvent | null = null;
    let newEventId = uuidv4().replace(/-/g, '');

    if (shouldCreateInAppleCalendar) {
      createdAppleEvent = await saveEventToAppleCalendarDesktop(
        transformEventForAppleCalendarDesktop(eventData as Partial<CalendarEvent>),
        targetCalendar?.internalGCalendarId
      );

      if (!createdAppleEvent?.id) {
        throw new Error("Couldn't create that event in Apple Calendar.");
      }

      newEventId = createdAppleEvent.id;
    }

    await createCalendarEventV2Async({
      calendarId: mainCalendar.calendarId,
      userId: user.userId,
      newEventOption: {
        ...eventData,
        eventId: newEventId,
        color: mainCalendar?.color || primaryColor,
        ...(createdAppleEvent ? { rawJson: JSON.stringify(createdAppleEvent) } : {}),
      },
    });
  };

  // Update calendar event for a task
  const updateTaskEvent = async (
    taskInfo: Partial<FirestoreTaskItem> | TaskModifyOption,
    boardId: string,
    taskId: string,
    appFrom: string,
    existingEvent: {
      eventId: string;
      calendarId?: string;
      unSyncToCalendar?: boolean;
      rawJson?: unknown;
    }
  ) => {
    if (!mainCalendar?.calendarId || !user?.userId) {
      return;
    }

    const eventData = buildTaskEventData(taskInfo, boardId, taskId, appFrom, {
      eventId: existingEvent.eventId,
      unSyncToCalendar: !!existingEvent.unSyncToCalendar,
      description: 'Updated from Lifestack (https://lifestack.ai)',
      includeEventTime: !!taskInfo.dueDateTime?.datetime && !!taskInfo.duration?.amount,
    });

    if (!eventData) {
      return;
    }

    const targetCalendarId = existingEvent.calendarId || mainCalendar.calendarId;
    const targetCalendar = findCalendarItem(targetCalendarId);
    const shouldUpdateAppleCalendar =
      canUseAppleDesktopBridge && isAppleEvent(targetCalendarId, findCalendarItem);

    let updatedAppleEvent: NativeAppleEvent | null = null;
    if (shouldUpdateAppleCalendar) {
      const nativeEventId =
        extractNativeAppleEventId({
          eventId: existingEvent.eventId,
          rawJson: existingEvent.rawJson,
        } as Partial<CalendarEvent>) || existingEvent.eventId;
      const recurrenceExplicitlySet = Object.prototype.hasOwnProperty.call(eventData, 'recurrence');

      const nativeUpdateResult = await updateEventInAppleCalendarDesktop(
        nativeEventId,
        transformEventForAppleCalendarDesktop(eventData as Partial<CalendarEvent>),
        {
          calendarIdentifier: targetCalendar?.internalGCalendarId,
          recurrenceExplicitlySet,
        }
      );

      if (!nativeUpdateResult.success || !nativeUpdateResult.appleEvent) {
        throw new Error("Couldn't update that event in Apple Calendar.");
      }

      updatedAppleEvent = nativeUpdateResult.appleEvent;
    }

    await updateCalendarEvent({
      calendarId: targetCalendarId,
      eventId: existingEvent.eventId,
      userId: user.userId,
      newEventOption: {
        ...eventData,
        ...(updatedAppleEvent ? { rawJson: JSON.stringify(updatedAppleEvent) } : {}),
      },
    });
  };

  // Find and delete event associated with a task
  const deleteTaskEvent = async (
    taskId: string,
    boardId: string,
    dueDateTime?: TaskItem['dueDateTime'],
    existingEvent?: {
      eventId: string;
      calendarId?: string;
      unSyncToCalendar?: boolean;
    }
  ) => {
    if (!user?.userId) {
      return;
    }

    const deleteResolvedEvent = async (event: CalendarEvent) => {
      if (canUseAppleDesktopBridge && isAppleEvent(event.calendarId!, findCalendarItem)) {
        const nativeEventId = extractNativeAppleEventId(event) || event.eventId!;
        const deletedFromApple = await deleteRecurringEventFromAppleCalendarDesktop(nativeEventId);

        if (!deletedFromApple) {
          throw new Error("Couldn't delete that event from Apple Calendar.");
        }
      }

      await deleteCalendarEvent({
        calendarId: event.calendarId!,
        eventId: event.eventId!,
        userId: user.userId,
        unSyncToCalendar: !!event.unSyncToCalendar,
      });
    };

    if (existingEvent?.eventId && existingEvent.calendarId) {
      const cachedEventDetails = dueDateTime
        ? findEventsInCacheByDate(getDueDateYYYYMMDD(dueDateTime) ?? '', user.userId).find(
            (event) => event.eventId === existingEvent.eventId
          )
        : undefined;

      await deleteResolvedEvent({
        eventId: existingEvent.eventId,
        calendarId: existingEvent.calendarId,
        unSyncToCalendar: existingEvent.unSyncToCalendar,
        rawJson: cachedEventDetails?.rawJson,
      } as CalendarEvent);
      return;
    }

    if (!dueDateTime?.datetime) {
      return;
    }

    const dueDateStr = getDueDateYYYYMMDD(dueDateTime);
    if (!dueDateStr) {
      return;
    }

    const eventsOnDate = findEventsInCacheByDate(dueDateStr, user.userId);
    const associatedEvent = eventsOnDate.find(
      (event) => event.taskFrom?.taskId === taskId && event.taskFrom?.boardId === boardId
    );

    if (associatedEvent?.eventId && associatedEvent.calendarId) {
      await deleteResolvedEvent(associatedEvent as CalendarEvent);
    }
  };

  const deleteTask = useCallback(
    async ({
      taskId,
      boardId,
      originalTaskId,
      mutateParam,
      shouldReflectToEvent,
      dueDateTime,
    }: DeleteTodoTaskParams) => {
      if (!taskId || !boardId) {
        return;
      }
      const resolvedAppFrom = findProject(boardId)?.appFrom;
      if (resolvedAppFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
        return;
      }

      try {
        if (resolvedAppFrom === 'apple-reminders' && originalTaskId) {
          await deleteAppleReminderDesktop(originalTaskId);
        }

        await deleteTodoTaskMutation({
          userId: user.userId,
          taskId,
          boardId,
          mutateParam,
        });
        setEventsSnackbar({ message: translateI18n('task.snackbar.deleted'), type: 'success', icon: 'task' });

        if (shouldReflectToEvent && dueDateTime) {
          await deleteTaskEvent(taskId, boardId, dueDateTime);
        }
      } catch (error) {
        console.error('Failed to delete task:', error);
        setEventsSnackbar({ message: translateI18n('task.snackbar.deleteFailed'), type: 'error', icon: 'task' });
        throw error;
      }
    },
    [deleteTodoTaskMutation, user.userId, setEventsSnackbar, findProject]
  );

  const addTodoTask = useCallback(
    async ({ boardId, newTaskInfo, taskId, shouldReflectToEvent }: AddTodoTaskParams) => {
      if (newTaskInfo.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
        return;
      }
      const addedToCalendar = getAddedToCalendar(shouldReflectToEvent, newTaskInfo);
      try {
        let originalTaskId: string | undefined;

        if (newTaskInfo.appFrom === 'apple-reminders') {
          const project = findProject(boardId);
          const hasDateOnly = !!newTaskInfo.dueDateTime?.date && !newTaskInfo.dueDateTime?.datetime;
          const dueDate = newTaskInfo.dueDateTime?.datetime || newTaskInfo.dueDateTime?.date;
          const applePriority = mapTaskPriorityToApplePriority(newTaskInfo.priority);
          const appleRecurrenceRule = recurrenceStringToAppleRule(newTaskInfo.recurring?.string);

          const createdReminder = await createAppleReminderDesktop({
            title: newTaskInfo.name || '',
            notes: newTaskInfo.desc,
            ...(dueDate && { dueDate, allDay: hasDateOnly }),
            priority: applePriority,
            listIdentifier: project?.originalBoardId,
            ...(appleRecurrenceRule && { recurrenceRule: appleRecurrenceRule }),
          });
          originalTaskId = createdReminder?.id;
        }

        await addTodoTaskMutation({
          boardId,
          userId: user.userId,
          newTaskInfo: { ...newTaskInfo, addedToCalendar },
          taskId,
          originalTaskId,
        });

        trackEventMixpanel('create_task', {
          name: newTaskInfo.name,
          appFrom: newTaskInfo.appFrom,
        });
        setEventsSnackbar({ message: translateI18n('task.snackbar.created'), type: 'success', icon: 'task' });

        if (shouldReflectToEvent && taskId) {
          await createTaskEvent(newTaskInfo, boardId, taskId, newTaskInfo.appFrom);
        }
        return taskId;
      } catch (error) {
        console.error('Failed to create task:', error);
        setEventsSnackbar({ message: translateI18n('task.snackbar.createFailed'), type: 'error', icon: 'task' });
        throw error;
      }
    },
    [addTodoTaskMutation, user.userId, setEventsSnackbar, findProject]
  );

  const updateTodoTaskStatus = useCallback(
    async (params: UpdateTodoTaskStatusParams) => {
      const resolvedAppFrom = findProject(params.boardId)?.appFrom;
      if (resolvedAppFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
        return;
      }

      try {
        if (resolvedAppFrom === 'apple-reminders' && params.originalTaskId) {
          await completeAppleReminderDesktop(params.originalTaskId, params.completed);
        }

        await updateTodoTaskStatusMutation({ ...params, userId: user.userId });
        trackEventMixpanel('update_todo_task_status');
        setEventsSnackbar({
          message: translateI18n(
            params.completed ? 'task.snackbar.completed' : 'task.snackbar.incompleted'
          ),
          type: 'success',
          icon: 'task',
        });
      } catch (error) {
        console.error('Failed to update task status:', error);
        setEventsSnackbar({ message: translateI18n('task.snackbar.statusUpdateFailed'), type: 'error', icon: 'task' });
        throw error;
      }
    },
    [updateTodoTaskStatusMutation, user.userId, setEventsSnackbar, findProject]
  );

  const updateTodoTask = useCallback(
    async ({
      taskId,
      boardId,
      newTaskInfo,
      movedTaskParam,
      showSnackbar = true,
      shouldReflectToEvent,
      recurrenceChanged,
      prevTaskInfo,
    }: Omit<UseUpdateTodoTaskParams, 'userId'> & {
      showSnackbar?: boolean;
      shouldReflectToEvent?: boolean;
      recurrenceChanged?: boolean;
      prevTaskInfo?: Partial<FirestoreTaskItem>;
    }) => {
      const resolvedAppFrom = newTaskInfo.appFrom ?? findProject(boardId)?.appFrom;
      if (resolvedAppFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
        return;
      }

      const cachedPrevTaskInfo = findInCacheByDate(
        movedTaskParam?.prevDate || getDueDateYYYYMMDD(newTaskInfo.dueDateTime),
        boardId
      ).find((task) => task.taskId === taskId);
      const resolvedPrevTaskInfo = prevTaskInfo ?? cachedPrevTaskInfo;

      const decision = determineTaskEventAction({
        newTaskInfo,
        prevTaskInfo: resolvedPrevTaskInfo,
        taskId,
        boardId,
        userId: user?.userId ?? '',
        findEventsByDate: findEventsInCacheByDate,
      });

      if (!user?.userId) {
        return;
      }

      const addedToCalendar =
        decision.action === 'delete'
          ? false
          : newTaskInfo.addedToCalendar === undefined
            ? !!shouldReflectToEvent
            : newTaskInfo.addedToCalendar;

      try {
        if (resolvedAppFrom === 'apple-reminders' && newTaskInfo.originalTaskId) {
          const project = findProject(boardId);
          const hasDateOnly = !!newTaskInfo.dueDateTime?.date && !newTaskInfo.dueDateTime?.datetime;
          const dueDate = newTaskInfo.dueDateTime?.datetime || newTaskInfo.dueDateTime?.date;
          const hasDueDate = !!dueDate;
          const reminderTitle = newTaskInfo.name ?? resolvedPrevTaskInfo?.name;
          const applePriority = mapTaskPriorityToApplePriority(newTaskInfo.priority);
          const recurrenceString = newTaskInfo.recurring?.string?.trim();
          const didRecurrenceChange = recurrenceChanged === true;
          const appleRecurrenceRule = didRecurrenceChange
            ? recurrenceStringToAppleRule(recurrenceString)
            : null;
          const shouldClearRecurrence =
            didRecurrenceChange && (!newTaskInfo.recurring?.isRecurring || !recurrenceString);

          await updateAppleReminderDesktop({
            reminderId: newTaskInfo.originalTaskId,
            title: reminderTitle || '',
            notes: newTaskInfo.desc,
            priority: applePriority,
            listIdentifier: project?.originalBoardId,
            ...(hasDueDate ? { dueDate, allDay: hasDateOnly } : { clearDueDate: true }),
            ...(didRecurrenceChange && appleRecurrenceRule
              ? { recurrenceRule: appleRecurrenceRule }
              : {}),
            ...(shouldClearRecurrence ? { clearRecurrenceRule: true } : {}),
          });
        }

        await updateTodoTaskMutation({
          taskId,
          boardId,
          userId: user.userId,
          newTaskInfo: {
            ...newTaskInfo,
            addedToCalendar,
          },
          ...(movedTaskParam ? { movedTaskParam } : {}),
        });
        if (showSnackbar) {
          setEventsSnackbar({ message: translateI18n('task.snackbar.updated'), type: 'success', icon: 'task' });
        }

        if (shouldReflectToEvent && user?.userId && mainCalendar?.calendarId) {
          if (decision.action === 'delete') {
            await deleteTaskEvent(
              taskId,
              boardId,
              resolvedPrevTaskInfo?.dueDateTime,
              decision.existingEvent ?? undefined
            );
            return;
          }

          if (decision.action === 'create') {
            await createTaskEvent(newTaskInfo, boardId, taskId, newTaskInfo.appFrom || 'lifestack');
          } else if (decision.action === 'update' && decision.existingEvent?.eventId) {
            const eventIdToMatch = decision.existingEvent.eventId;
            const dateCandidates = [
              movedTaskParam?.prevDate,
              getDueDateYYYYMMDD(cachedPrevTaskInfo?.dueDateTime),
              getDueDateYYYYMMDD(newTaskInfo.dueDateTime),
            ].filter((d): d is string => !!d);

            let existingEventDetails = undefined as CalendarEvent | undefined;
            for (const date of dateCandidates) {
              const found = findEventsInCacheByDate(date, user.userId).find(
                (event) => event.eventId === eventIdToMatch
              );
              if (found) {
                existingEventDetails = found;
                break;
              }
            }

            if (!existingEventDetails) {
              existingEventDetails = findEventsInCache({ userId: user.userId }).find(
                (event) => event.eventId === eventIdToMatch
              );
            }
            await updateTaskEvent(
              newTaskInfo,
              boardId,
              taskId,
              newTaskInfo.appFrom || 'lifestack',
              {
                eventId: decision.existingEvent.eventId,
                calendarId: decision.existingEvent.calendarId,
                unSyncToCalendar: !!decision.existingEvent.unSyncToCalendar,
                rawJson: existingEventDetails?.rawJson,
              }
            );
          }
        }
      } catch (error) {
        console.error('Failed to update task:', error);
        if (showSnackbar) {
          setEventsSnackbar({ message: translateI18n('task.snackbar.updateFailed'), type: 'error', icon: 'task' });
        }
        throw error;
      }
    },
    [
      updateTodoTaskMutation,
      user.userId,
      setEventsSnackbar,
      findProject,
      findInCacheByDate,
      findEventsInCache,
      findEventsInCacheByDate,
    ]
  );

  return {
    isUpdatingTask,
    updateTodoTask,
    addTodoTask,
    isAddingTask,
    deleteTask,
    isDeletingTask,
    isTaskStatusUpdating,
    updateTodoTaskStatus,
  };
};
